import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  string, bool, shape, number, func
} from 'prop-types';
import {
  ExperienceContext,
  useStore,
  useMVCSID,
  useConfigService
} from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { Carousel, Placeholder, Row } from '@thd-olt-component-react/core-ui';
import { ProductPod, ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import {
  useDataModel,
  extend,
  params,
  string as dString,
  shape as dShape,
  arrayOf,
  QueryProvider,
} from '@thd-nucleus/data-sources';

import { SponsoredProductPod } from '../SponsoredProductPod';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import {
  getSchema,
  getSkus,
  isExperienceDataResolved,
  mapSearchModel,
  showCarouselPlaceholder,
  triggerNewRelic
} from '../../utils/helpers';

import './SponsoredCarousel.scss';
import { carouselTypes, PRODUCT } from '../../constants/constants';

const propTypes = {
  customRenderId: string,
  slugId: string,
  hideTitle: bool,
  isSinglePLA: bool,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
    schema: string,
    slugId: string
  }),
  slidesPer: number,
  noATCFulfillment: bool,
  utilizeRvData: bool,
  mockEnabledFeatureSwitch: bool,
  showSponsoredCarousel: bool,
  onNoSponsoredProducts: func,
  hideCarouselArrows: bool
};

const defaultProps = {
  customRenderId: '',
  slugId: '',
  hideTitle: false,
  pageContext: {},
  slidesPer: null,
  noATCFulfillment: null,
  utilizeRvData: false,
  mockEnabledFeatureSwitch: false,
  showSponsoredCarousel: false,
  onNoSponsoredProducts: undefined,
  isSinglePLA: false,
  hideCarouselArrows: false
};
const SponsoredCarouselComponent = (props) => {
  const {
    customRenderId,
    slugId,
    pageContext,
    hideTitle,
    isSinglePLA,
    slidesPer,
    noATCFulfillment,
    mockEnabledFeatureSwitch,
    showSponsoredCarousel,
    onNoSponsoredProducts,
    hideCarouselArrows
  } = props;
  const browserId = useMVCSID();
  const experienceContext = useContext(ExperienceContext);
  const renderNewCarouselPip = useConfigService('fs-prop:pip-master__sponsored-carousel-nucleus--renderNewCarousel') || false;
  const renderNewCarouselBrowseAndSearch = useConfigService('fs-prop:browse-and-search-master__sponsored-carousel-nucleus--renderNewCarousel') || false;
  let renderNewCarousel = renderNewCarouselPip || renderNewCarouselBrowseAndSearch;

  const { storeId, isLocalized } = useStore({
    varnish: true,
    online: true
  });
  const { utilizeRvData } = props;
  let breakpoints = {};
  if (!slidesPer) {
    breakpoints = Carousel.defaultProps.breakpoints;
  } else {
    breakpoints = {
      sm: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      md: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      lg: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      xl: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
    };
  }

  const { schema, schemaId } = useMemo(() => {
    const schemaName = getSchema({ schema: pageContext?.schema, pageContext, props });
    return { schema: schemaName, schemaId: schemaName + '__carousel' };
  }, [props]);
  const isMobile = useMemo(() => (experienceContext.channel === 'mobile'), [experienceContext.channel]);
  const titleClassname = useMemo(() => {
    return `nucleus-carousel__title__text ${isMobile ? 'nucleus-carousel__title__text--medium' : ''}`;
  }, [isMobile]);

  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [pageId, setPageId] = useState('');
  const [products, setProducts] = useState([]);
  const [atcFilteredProducts, setATCFilteredProducts] = useState([]);
  const [carouselData, setCarouselData] = useState({});
  const [serverError, setServerError] = useState(false);
  const [loadingState, setLoadingState] = useState(true);

  const setAdProducts = useCallback((adProducts) => {
    if (adProducts && adProducts.length) {
      setProducts(adProducts
        .map((product) => ({
          ...product,
          onViewBeacon: product.onViewBeacon ? product.onViewBeacon.replace('https:', '') : '',
          ITC: `AUC-${product?.campaignId || ''}-${product?.placementId || ''}-${product?.slotId || ''}`
        }))
      );
    }
  }, []);

  if (showSponsoredCarousel
    || mockEnabledFeatureSwitch
    || schema === 'hp_sponsored_auth'
    || schema === 'hp_sponsored'
    || schema === 'cat_sponsored') {
    renderNewCarousel = true;
  }

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });
    const { label: pageLabel, isPip, isStorePage } = pageContext || {};
    const isStandardCarousel = (
      pageLabel === 'browse-search'
      || isPip
      || isStorePage
      || schema === 'hp_sponsored'
      || schema === 'hp_sponsored_auth'
      || schema === 'cat_sponsored'
    );

    if (renderNewCarousel && isResolvedContext && isStandardCarousel) {

      const isValidScenario = shouldMakeApiRequest({
        lastRequestScenario,
        experienceContext,
        pageContext,
        customRenderId,
        browserId,
        schema
      });

      if (isStandardCarousel && isValidScenario) {
        setLoadingState(true);
        setLastRequestScenario({
          url: window.location.pathname + window.location.search,
          skus: getSkus(pageContext)
        });

        makeApiRequest({
          browserId,
          schema,
          schemaId,
          experienceContext,
          pageContext,
          carouselType: pageContext?.isPip && isSinglePLA ? carouselTypes.PLA : null,
          show: PRODUCT,
          setCarouselData,
          setPageId,
          setAdProducts,
          utilizeRvData,
          slugId,
          setServerError,
          onNoSponsoredProducts
        });
      } else {
        setLoadingState(false);
      }
    }

  }, [pageContext?.data, isLocalized, experienceContext.mounted, slugId, browserId]);

  const skip = !products?.length;
  const resp = useDataModel('searchModel', {
    ssr: false,
    skip,
    variables: {
      itemIds: products?.map((item) => item.id.toString()) || [],
      storeId,
    },
  });

  const { data, loading, error } = resp;

  useEffect(() => {
    initImpressionWatchers(['NUCLEUS_CAROUSEL']);
  }, [data]);
  useEffect(() => {
    // need to set loading to false if searchModel or API throws error
    if (error || serverError) {
      setLoadingState(false);
      onNoSponsoredProducts(true);
    }
    const filteredProducts = [];
    let productsRemovedCounter = 0;
    const { targetingSource, adsFiltered = 0 } = carouselData;
    if (products && products.length && data?.searchModel?.products?.length) {
      products.forEach((product) => {
        const productData = data.searchModel.products.find(({ itemId }) => itemId === `${product.id}`);

        if (productData) {
          const isATCEnabled = ProductPodUtils.isATCEnabled(productData);

          if (isATCEnabled) {
            const updatedProduct = mapSearchModel(product, productData);
            filteredProducts.push(updatedProduct);
          } else {
            productsRemovedCounter += 1;

          }
        }
      });
      setATCFilteredProducts(filteredProducts);
      setLoadingState(false);
    }

    if (productsRemovedCounter > 0) {
      triggerNewRelic(`sponsoredBottomCarousel-adsFiltered-${targetingSource}`,
        adsFiltered + productsRemovedCounter);
    }
    if (onNoSponsoredProducts && (data !== undefined && !loadingState) && filteredProducts.length <= 0) {
      onNoSponsoredProducts(true);
    }
  }, [products, data, error, serverError, carouselData?.targetingSource]);

  if (showCarouselPlaceholder(renderNewCarousel, experienceContext, pageContext, loadingState)) {
    let slides = [];
    let textPlaceholder = [...Array(16)].map((value, idx) => <Placeholder type="text" key={idx.toString()} />);
    for (let i = 0; i < 16; i += 1) {
      slides.push(
        <div key={i.toString()} style={{ width: '100%' }}>
          <Placeholder
            type="rect"
            height="150px"
            width="100%"
          />
          {textPlaceholder}
        </div>
      );
    }
    return (
      <Row data-component="SponsoredCarouselPlaceholder">
        <Row>
          <Carousel breakpoints={breakpoints} hideCarouselArrows={hideCarouselArrows}>
            {slides}
          </Carousel>
        </Row>
      </Row>
    );
  }

  return (
    <>
      {(!serverError && atcFilteredProducts.length && renderNewCarousel) ? (
        <div id="sponsored-carousel-nucleus" className="col__12-12" data-component="SponsoredCarousel">
          <div
            id={schema}
            className="nucleus-carousel"
            data-testid="sponsored-pla-carousel"
          >
            <meta data-prop="name" content={schema} />
            {!hideTitle && (
              <div className="nucleus-carousel__title">
                <h2 className={titleClassname}>
                  Sponsored Products
                </h2>
              </div>
            )}
            <Carousel breakpoints={breakpoints} hideCarouselArrows={hideCarouselArrows}>
              {atcFilteredProducts.map((product, idx) => {
                return (
                  <SponsoredProductPod
                    key={product.id}
                    product={product}
                    pageId={pageId}
                    storeId={storeId}
                    position={idx + 1}
                    schema={schema}
                    noATCFulfillment={noATCFulfillment}
                    targetingSource={carouselData?.targetingSource}
                  />
                );
              })}
            </Carousel>
          </div>
        </div>
      ) : null}
    </>
  );

};

SponsoredCarouselComponent.propTypes = propTypes;
SponsoredCarouselComponent.defaultProps = defaultProps;

SponsoredCarouselComponent.dataModel = {
  searchModel: params({
    itemIds: arrayOf(dString()),
  }).shape({
    products: arrayOf(dShape(ProductPod.dataModel.product))
  })
};

/* eslint react/jsx-props-no-spreading: 0 */
const QueryProvidedSponsoredCarousel = (props) => (
  <QueryProvider cacheKey="sponsored-nucleus-carousel">
    <SponsoredCarouselComponent {...props} />
  </QueryProvider>
);

QueryProvidedSponsoredCarousel.propTypes = propTypes;

QueryProvidedSponsoredCarousel.defaultProps = defaultProps;

QueryProvidedSponsoredCarousel.dataModel = extend({}, SponsoredCarouselComponent);

const SponsoredCarousel = withErrorBoundary(
  withDynamicComponent(
    withHydrator(
      {
        id: 'sponsored-carousel-nucleus',
        preserveCtxVal: 'clientStore'
      },
      QueryProvidedSponsoredCarousel
    )
  )
);

export { SponsoredCarousel };
